import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import notify from '~/components/Notify'
import FutureContractsService from '~/api/services/futureContracts'
import { Button, Comment, Dimmer, Form, Loader, Popup } from 'semantic-ui-react'
import LoaderMilkCan from '~/components/Loader'
import dayjs from 'dayjs'
import CustomIcon from '~/components/CustomIcon'
import Dialog from '~/components/Dialog'
import { useAuth } from '~/routes/components/AuthProvider'
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants'
import { ThemeContext } from 'styled-components'
import { CommentWrapper, FormWrapper, StyledLabel } from './style'
import { handleErrors } from '~/utils/helpers'

interface ContractsCommentsProps {
  contractId: string
}

const ContractsComments: React.FC<ContractsCommentsProps> = (props) => {
  const { contractId } = props
  const { t } = useTranslation()
  const { firstColor } = useContext(ThemeContext)
  const { systemData: { logged: { roleSlug, id } } } = useAuth()
  const [isBusy, setIsBusy] = useState(false)
  const [page, setPage] = useState(0)
  const pageRef = useRef(null)
  const [hasMore, setHasMore] = useState(true);
  const [loadingComment, setLoadingComment] = useState(false)
  const [isLoadingDelete, setIsLoadingDelete] = useState(false)
  const [loadingNewComments, setLoadingNewComments] = useState(false)
  const [deleteCommentId, setDeleteCommentId] = useState(null)
  const [comment, setComment] = useState('')
  const commentWrapperRef = useRef<HTMLDivElement>(null)
  const [editComment, setEditComment] = useState(null)
  const [comments, setComments] = useState([])
  const roleToEditDeleteComments = roleSlug == ADMIN_ROLE_SLUG
  const operativeUserLogged = roleSlug === OPERATIVE_ROLE_SLUG
  const permissionToAddComment = roleToEditDeleteComments || operativeUserLogged

  useEffect(() => {
    if (contractId !== '') {
      fetchComments()
    }
  }, [contractId])

  useEffect(() => {
    pageRef.current = page
  }, [page])

  useEffect(() => {
    const divElement = commentWrapperRef?.current?.parentElement;

    if (!divElement) {
      return
    }

    function handleScroll() {
      const { scrollTop } = divElement;
      if (scrollTop === 0 && hasMore && contractId) {
        loadMoreComments()
      }
    }

    divElement.addEventListener('scroll', handleScroll);
    return () => {
      divElement.removeEventListener('scroll', handleScroll);
      setPage(0)
    };
  }, [hasMore, contractId])

  useEffect(() => {
    if (comments.length > 0 && pageRef?.current === 0) {
      const divElement = commentWrapperRef?.current?.parentElement;
      setHasMore(true)
      if (divElement) {
        divElement.scrollTop = divElement.scrollHeight - divElement.clientHeight;
      }
    }
  }, [comments])

  async function loadMoreComments() {
    setLoadingNewComments(true)
    try {
      const response = await FutureContractsService.getContractComments(
        contractId,
        {
          rows: 5,
          page: pageRef?.current + 1
        }
      )
      if (response.data?.length === 0) {
        setHasMore(false)
      } else {
        setPage(pageRef.current + 1)
        const reversed = response.data.reverse()
        setComments((prevOptions) => [...reversed, ...prevOptions]);
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoadingNewComments(false)
  }

  async function fetchComments() {
    setIsBusy(true)
    try {
      const response = await FutureContractsService.getContractComments(
        contractId,
        {
          rows: 5,
          page: 0
        }
      )
      setComments([...response.data].reverse())
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleSaveComment(comment, edit = false) {
    setLoadingComment(true)
    try {
      if (!edit) {
        setComments([...comments, { content: comment }])
        const response = await FutureContractsService.storeContractComment(contractId, { content: comment })
        setComment('')
        setComments(prevArray => {
          const arrayTemp = [...prevArray]
          arrayTemp[arrayTemp.length - 1] = response.data
          return arrayTemp
        })
      } else if (edit) {
        await FutureContractsService.updateContractComment(contractId, comment?.id, { content: editComment?.content })
        const index = comments.findIndex(comment => comment?.id === editComment?.id)
        const array = [...comments]
        array[index] = editComment
        setComments(array)
      }
      setEditComment(null)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingComment(false)
  }

  function renderEditComment(isVisible) {
    return <Form reply className={`form-container ${isVisible ? 'visible' : ''}`}>
      <div style={{ marginBottom: '1em' }}>
        <Form.TextArea
          style={{ height: '7em', margin: 0 }}
          value={editComment?.content}
          error={editComment?.content?.length > 1000}
          //@ts-ignore
          onChange={(e, data) => setEditComment({ ...editComment, content: data.value })}
        />
        <small>{`${editComment?.content?.trim().length} / 1000`}</small>
      </div>
      <Popup
        content={t('Update comment')}
        trigger={<CustomIcon
          name='checkmark'
          disabled={(editComment?.content?.length > 1000 || editComment?.content?.trim().length === '') || loadingComment}
          color='green'
          onClick={() => handleSaveComment(editComment, true)}
        />}
      />
      <Popup
        content={t('Close update')}
        trigger={<CustomIcon
          name='close'
          color='red'
          onClick={() => setEditComment(null)}
        />}
      />
    </Form>
  }

  function renderCommentContent(comment) {
    return <CommentWrapper>
      <StyledLabel color={firstColor}>{comment?.content}</StyledLabel>
      {!comment?.id && <Loader active={loadingComment} size='small' />}
    </CommentWrapper>
  }

  function renderComments() {
    return comments.map(comment => {
      const loggedUsercomment = operativeUserLogged && comment.userId === id
      return <Comment key={comment?.id} style={{ marginTop: '0.8em' }}>
        <Comment.Content>
          <Comment.Author as='a'>{comment?.userName}</Comment.Author>
          <Comment.Metadata>
            <div>{dayjs(comment?.createdAt).format('ddd, MMM D, YYYY HH:mm')}</div>
          </Comment.Metadata>
          <Comment.Text>
            <FormWrapper>
              {renderEditComment(comment?.id && editComment?.id === comment?.id)}
            </FormWrapper>
            {(!comment?.id || editComment?.id !== comment?.id) && renderCommentContent(comment)}
          </Comment.Text>
          {editComment?.id !== comment?.id && <Comment.Actions>
            {(roleToEditDeleteComments || loggedUsercomment) && <Comment.Action>
              <Popup
                content={t('Edit')}
                trigger={<CustomIcon name='pencil' onClick={() => setEditComment(comment)} />}
              />
              {roleToEditDeleteComments && <Popup
                content={t('Delete')}
                trigger={<CustomIcon name='trash' color='red' onClick={() => setDeleteCommentId(comment?.id)} />}
              />
              }
            </Comment.Action>}
          </Comment.Actions>
          }
        </Comment.Content>
      </Comment>
    })
  }

  function renderAddCommentForm() {
    return <Form reply>
      <div style={{ marginBottom: '1em' }}>
        <Form.TextArea
          style={{ height: '7em', margin: 0 }}
          error={comment.length > 1000}
          value={comment}
          //@ts-ignore
          onChange={(e, data) => setComment(data.value)}
        />
        <small >{`${comment.length} / 1000`}</small>
      </div>
      <Button
        type='button'
        content={t('Add comment')}
        labelPosition='left'
        disabled={(comment.length > 1000 || comment.trim().length === 0) || loadingComment}
        icon='edit'
        onClick={() => handleSaveComment(comment)}
        primary
      />
    </Form>
  }

  async function handleDeleteComment() {
    setIsLoadingDelete(true);
    try {
      await FutureContractsService.deleteContractComment(contractId, deleteCommentId)
      setComments(comments.filter(comment => comment?.id !== deleteCommentId))
      setDeleteCommentId(null)
      notify(t('Successfully delete comment'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingDelete(false);
  }

  function renderDeleteModal() {
    const comment = comments.find(comment => comment?.id === deleteCommentId)
    return <Dialog
      textContent={t('Do you want to delete comment created by {{name}}', { name: comment?.userName })}
      handleClose={() => setDeleteCommentId(null)}
      textHeader={t('Delete comment')}
      isLoading={isLoadingDelete}
      handleSubmit={() => handleDeleteComment()}
      textSubmit={t('Delete')}
      textClose={t('Close')}
      isDialogOpen={Boolean(deleteCommentId)}
      type='abort'
    />
  }

  return <div style={{ position: 'relative' }}>
    <Dimmer inverted active={isBusy}>
      <LoaderMilkCan isLoading={isBusy} />
    </Dimmer>
    <Comment.Group>
      <div ref={commentWrapperRef}>
        <div style={{ height: 20, position: 'absolute', top: 0, width: '100%' }}>
          <Dimmer inverted active={loadingNewComments}>
            <Loader active={loadingNewComments}>{t('Loading')}</Loader>
          </Dimmer>
        </div>
        {renderComments()}
      </div>
    </Comment.Group>
    {permissionToAddComment && renderAddCommentForm()}
    {renderDeleteModal()}
  </div>
}
export default ContractsComments